<template>
    <div class="card">
        <header class="card-header">
            <div class="card-header-title">
                <div class="tw:flex tw:justify-between tw:w-full">
                    <slot name="header">{{ translate('announcements') }}</slot>

                    <div v-if="!($isMobile || $isTablet)" class="media-right">
                        <div v-if="displayForm" class="control">
                            <e-button type="is-danger" @click="cancel" @keyup="cancel">
                                {{ translate('cancel') }}
                            </e-button>
                        </div>

                        <e-button
                            v-if="previewMode"
                            type="is-primary"
                            :class="{ 'is-outlined': previewActionOutline }"
                        >
                            <slot name="action">{{ translate('add_announcement') }}</slot>
                        </e-button>
                        <e-button
                            v-if="canPost && !displayForm && !previewMode"
                            :disabled="displayForm"
                            type="is-primary"
                            @click="toggle"
                            @keyup="toggle"
                        >
                            <slot name="action">{{ translate('add_announcement') }}</slot>
                        </e-button>
                        <e-button
                            v-if="!canPost && hasInstructor && !previewMode"
                            type="is-primary"
                            outlined
                            @click="openInstructorChat"
                            @keyup="openInstructorChat"
                        >
                            {{ translate('contact_instructor') }}
                        </e-button>
                    </div>

                    <div v-if="($isMobile || $isTablet) && !displayForm">
                        <e-button
                            v-if="canPost"
                            type="is-primary"
                            class="tw:w-full"
                            @click="toggle"
                            @keyup="toggle"
                        >
                            <slot name="action">{{ translate('add_announcement_mobile') }}</slot>
                        </e-button>
                        <e-button
                            v-if="!canPost && hasInstructor"
                            class="is-outlined tw:w-full"
                            outlined
                            type="is-primary"
                            @click="openInstructorChat"
                            @keyup="openInstructorChat"
                        >
                            {{ translate('contact_instructor') }}
                        </e-button>
                    </div>
                </div>
            </div>
        </header>

        <div class="card-content">
            <!-- Desktop: Inline Form -->
            <div v-if="canPost && displayForm && !($isMobile || $isTablet)" class="media">
                <announcement-form
                    v-model:title="title"
                    v-model:message="message"
                    v-model:send-email-copy="sendEmailCopy"
                    v-model:important="important"
                    v-model:edit-announcement-date="editAnnouncementDate"
                    v-model:announcement-date="announcementDate"
                    :media="media"
                    :submitting="submitting"
                    :not-visible-to-students="notVisibleToStudents"
                    :max-date-time="maxDateTime"
                    :user="user"
                    @add-attachment="addAttachment"
                    @prompt-date-change="promptDateChange"
                />
            </div>

            <!-- Mobile: Bottom Drawer -->
            <e-sidebar
                v-if="$isMobile || $isTablet"
                v-model="displayForm"
                position="bottom"
                fullscreen
                overlay
                :can-cancel="['escape', 'outside']"
                :on-cancel="cancel"
                class="announcement-drawer"
            >
                <div class="tw:p-4">
                    <div class="tw:flex tw:justify-between tw:items-center tw:mb-4">
                        <div class="tw:font-semibold tw:text-3xl"><slot name="action">{{ translate('add_announcement') }}</slot></div>
                        <e-button icon-right="times" @click="cancel" />
                    </div>

                    <div class="">
                        <announcement-form
                            v-model:title="title"
                            v-model:message="message"
                            v-model:send-email-copy="sendEmailCopy"
                            v-model:important="important"
                            v-model:edit-announcement-date="editAnnouncementDate"
                            v-model:announcement-date="announcementDate"
                            :user="user"
                            :media="media"
                            :submitting="submitting"
                            :not-visible-to-students="notVisibleToStudents"
                            :max-date-time="maxDateTime"
                            @add-attachment="addAttachment"
                            @prompt-date-change="promptDateChange"
                        />
                    </div>
                </div>
            </e-sidebar>

            <hr v-if="canPost && displayForm && !isEmpty && !($isMobile || $isTablet)" />
            <div v-if="!isEmpty && !loading" class="card-list">
                <div v-for="(announcement, key) in announcements" :key="announcement.uuid">
                    <announcement
                        v-if="(key < 4) || viewMore"
                        :key="announcement.uuid"
                        :announcement="announcement"
                        :open="
                            announcementKey
                                ? key === announcementKey
                                : (!($isMobile || $isTablet) && key === 0) || announcement.important
                        "
                        :type="type"
                        :course="course"
                        :class="announcement.important ? 'is-important' : ''"
                        @edit-modal-open="onEditModalOpen"
                        @edit-modal-close="onEditModalClose"
                    />
                </div>

                <div v-if="announcements.length > 4" class="has-text-centered m-t-lmd">
                    <e-button type="is-primary" outlined @click="viewMore = !viewMore" @keyup="viewMore = !viewMore">
                        <span v-if="!($isMobile || $isTablet)">
                            {{
                                translate('view_hide_previous_announcements', {
                                    visibility: viewMore ? translate('hide') : translate('view'),
                                })
                            }}
                        </span>

                        <span v-else>
                            {{ viewMore ? translate('hide') : translate('view') }}
                        </span>
                    </e-button>
                </div>
            </div>

            <p v-if="isEmpty && !loading && !(canPost && displayForm)" class="has-text-subtle has-text-centered">
                <slot name="empty">{{ translate('no_announcements_to_display') }}</slot>
            </p>


            <div v-if="isEmpty && loading">
                <skeleton-card :active="loading" :content="{ display: false }" />
            </div>
        </div>
    </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty.js';
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';

import Announcement from '@/components/announcements/Announcement.vue';
import AnnouncementForm from '@/components/announcements/AnnouncementForm.vue';
import SkeletonCard from '@/components/skeleton-loader/skeleton-card/SkeletonCard.vue';
import { FILE_UPLOAD_CONTEXT } from '@/constants/fileUploadRejection.js';
import { handleResponseError, localDateToUTC } from '@/Helpers.js';
import Activity from '@/models/Activity.js';
import ActivityUpdate from '@/models/ActivityUpdate.js';
import Course from '@/models/Course.js';
import CourseAnnouncement from '@/models/CourseAnnouncement.js';
import moment from '@/plugins/vue-moment.js';
import { getAttachmentFile, postActivityUpdate, postCourseAnnouncement } from '@/services/announcementUpload.js';
import { announcementValidationMessage } from '@/utilities/announcementError.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';
import { reportFileUploadServerError } from '@/utils/reportFileUploadRejection.js';

export default {
    name: 'AnnouncementList',

    components: { Announcement, AnnouncementForm, SkeletonCard },

    props: {
        type: {
            required: true,
            type: String,
        },

        announcementKey: {
            type: [Number, String],
            default: null,
        },

        courseSettings: {
            type: Object,
            default: () => ({}),
        },

        courseId: {
            type: Number,
            required: true,
        },

        activityId: {
            type: Number,
            default: null,
        },

        previewMode: {
            type: Boolean,
            default: false,
        },

        previewActionOutline: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['close', 'fulfilled'],

    setup() {
        const { t: translate } = useI18n();

        return { translate };
    },

    data: () => ({
        loading: false,
        submitting: false,
        sendEmailCopy: false,
        displayForm: false,
        title: null,
        message: null,
        media: [],
        viewMore: false,
        editAnnouncementDate: false,
        announcementDate: null,
        dirty: false,
        important: false,
        editModalOpen: 0,
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        course() {
            return Course.find(this.courseId);
        },

        activity() {
            if (this.type !== 'Activity' || !this.activityId) {
                return null;
            }
            return Activity.query().with(['course', 'activity_settings']).find(this.activityId);
        },

        canPost() {
            if (!this.user) return false;

            if (this.type === 'Course' && this.course) {
                return this.$gate.allows('create', ['CourseAnnouncement', this.course]);
            }

            if (!this.activity) {
                return false;
            }

            return this.$gate.allows('create', ['ActivityUpdate', this.activity]);
        },

        announcements() {
            if (this.type === 'Activity') {
                return ActivityUpdate.query()
                    .with(['media', 'creator', 'activity.course'])
                    .where('activity_id', this.activityId)
                    .orderBy('important', 'desc')
                    .orderBy('published_at', 'desc')
                    .get();
            }

            return CourseAnnouncement.query()
                .with(['creator', 'media', 'course', 'read_receipts'])
                .where('course_id', this.courseId)
                .orderBy('important', 'desc')
                .orderBy('published_at', 'desc')
                .get();
        },

        isEmpty() {
            return !this.announcements.length;
        },

        isBackgroundRefreshPaused() {
            return this.displayForm || this.editModalOpen > 0;
        },

        hasInstructor() {
            return !isEmpty(this.instructor);
        },

        instructor() {
            if (!this.$store.getters['Courses/has'](this.courseId)) {
                return null;
            }

            return this.$store.getters[`Courses/${this.courseId}/get`]('instructor');
        },

        namespace() {
            return `${this.type}Announcements`;
        },

        payloadType() {
            return `${this.type.toLowerCase()}Id`;
        },

        payload() {
            return {
                course_id: this.courseId,
                activity_id: this.activityId,
                title: this.title,
                body: this.message,
                media: this.media,
                type: 1,
                published_at: localDateToUTC(this.announcementDate, this.$timezone.name)
                    .utc()
                    .format('YYYY-MM-DD HH:mm:ss'),
                send_notification: this.sendEmailCopy,
                notified_at: null,
                important: this.important,
            };
        },

        hasVisibleAtActivitySetting() {
            if (this.type === 'Activity') {
                return this.activity.activity_settings.visible_at !== null;
            }

            return false;
        },

        notVisibleToStudents() {
            if (this.type === 'Activity' && this.hasVisibleAtActivitySetting) {
                const visibleOn = moment.utc(this.activity.activity_settings.visible_at).tz(this.$timezone.name);
                const now = moment();

                return visibleOn.diff(now) > 0;
            }

            return false;
        },

        maxDateTime() {
            if (this.type === 'Activity') {
                return null;
            }

            return new Date(
                new Date(this.courseSettings.end_date).getFullYear(),
                new Date(this.courseSettings.end_date).getMonth(),
                new Date(this.courseSettings.end_date).getDate() + 30
            );
        },
    },

    watch: {
        title() {
            this.title ? (this.dirty = true) : (this.dirty = false);
        },

        message() {
            this.message ? (this.dirty = true) : (this.dirty = false);
        },

        media() {
            this.media.length ? (this.dirty = true) : (this.dirty = false);
        },

        isEmpty(empty) {
            if (this.previewMode || this.loading) {
                return;
            }

            this.syncFulfilledVisibility(!empty);
        },

        loading(isLoading, wasLoading) {
            if (this.previewMode || this.type !== 'Activity') {
                return;
            }

            if (wasLoading && !isLoading) {
                this.syncFulfilledVisibility(!this.isEmpty);
            }
        },

        canPost() {
            this.syncActivityUpdatesRefreshBinding();
        },
    },

    async created() {
        if (this.previewMode) {
            this.announcementDate = new Date();
            return;
        }

        if (this.$gate.allows('viewAny', ['CourseEnrollment', this.course])) {
            await this.$store.dispatch('ActiveEnrollment/ensure', this.courseId);
        }

        if (this.type === 'Course') {
            await this.loadCourseAnnouncements();
            this.$emit('fulfilled', !!this.announcements.length);
        } else if (this.activityId) {
            await this.loadActivityUpdates();
        }

        if (this.announcementKey && this.announcementKey >= 4) {
            this.viewMore = true;
        }

        this.announcementDate = new Date();
    },

    mounted() {
        this.syncActivityUpdatesRefreshBinding();
    },

    beforeUnmount() {
        this.unbindActivityUpdatesRefresh();
    },

    methods: {
        syncFulfilledVisibility(hasUpdates) {
            if (this.previewMode || this.type !== 'Activity') {
                return;
            }

            this.$emit('fulfilled', hasUpdates);
        },

        onEditModalOpen() {
            this.editModalOpen += 1;
        },

        onEditModalClose() {
            this.editModalOpen = Math.max(0, this.editModalOpen - 1);
        },

        bindActivityUpdatesRefresh() {
            if (this._activityUpdatesRefreshBound) {
                return;
            }

            this._refreshActivityUpdates = () => {
                if (!document.hidden) {
                    this.loadActivityUpdates({ silent: true });
                }
            };

            this._onVisibilityChange = () => {
                if (!document.hidden) {
                    this._refreshActivityUpdates();
                }
            };

            // visibilitychange misses some multi-window layouts; focus covers admin-delete in another window.
            this._onWindowFocus = () => {
                this.loadActivityUpdates({ silent: true });
            };

            document.addEventListener('visibilitychange', this._onVisibilityChange);
            window.addEventListener('focus', this._onWindowFocus);
            this._activityUpdatesRefreshBound = true;
        },

        unbindActivityUpdatesRefresh() {
            if (this._onVisibilityChange) {
                document.removeEventListener('visibilitychange', this._onVisibilityChange);
                this._onVisibilityChange = null;
            }

            if (this._onWindowFocus) {
                window.removeEventListener('focus', this._onWindowFocus);
                this._onWindowFocus = null;
            }

            this._activityUpdatesRefreshBound = false;
        },

        syncActivityUpdatesRefreshBinding() {
            if (this.previewMode || this.type !== 'Activity' || this.canPost) {
                this.unbindActivityUpdatesRefresh();
                return;
            }

            this.bindActivityUpdatesRefresh();
        },

        extractActivityUpdateIds(result) {
            const entities = result?.entities?.activity_updates;

            if (Array.isArray(entities)) {
                return entities.map(row => row.id).filter(id => id != null);
            }

            const payload =
                result?.response?.data?.data ?? result?.response?.data ?? result?.data?.data ?? result?.data;

            if (Array.isArray(payload)) {
                return payload.map(row => row.id).filter(id => id != null);
            }

            return [];
        },

        removeStaleActivityUpdates(activityId, result) {
            const retainedIds = new Set(this.extractActivityUpdateIds(result));

            ActivityUpdate.query()
                .where('activity_id', activityId)
                .get()
                .forEach(update => {
                    if (!retainedIds.has(update.id) && typeof update.$delete === 'function') {
                        update.$delete();
                    }
                });
        },

        addAttachment(media) {
            this.media = media;
        },

        /**
         * Resolves true when the announcement was actually created. Callers that reset form state
         * afterwards must branch on it: the catch below swallows the error, so this always
         * resolves and a caller cannot tell success from failure any other way.
         */
        async createAnnouncement() {
            if (this.previewMode) return false;

            this.submitting = true;

            let published = false;

            try {
                if (this.type === 'Activity') {
                    await postActivityUpdate(this.payload, this.media);
                } else {
                    await postCourseAnnouncement(this.courseId, this.payload, this.media);
                }

                // Set before clear() so a throw from the form reset cannot report a published
                // announcement as a failure.
                published = true;

                this.clear();
            } catch (e) {
                handleResponseError(e);

                this.alertValidationFailure(e);

                // Only a failed publish that carried files counts; a throw from clear() comes after success.
                const attachedFiles = published
                    ? []
                    : (this.media ?? []).filter(item => getAttachmentFile(item) !== null);

                if (attachedFiles.length > 0) {
                    reportFileUploadServerError(
                        FILE_UPLOAD_CONTEXT.ANNOUNCEMENT_ATTACHMENT_INSTRUCTOR,
                        attachedFiles,
                        e
                    );
                }
            }

            this.submitting = false;

            return published;
        },

        /**
         * Surface a rejected publish. handleResponseError() silences the global snackbar for
         * 422s and this form renders no field-level errors, so without this the user sees
         * nothing at all when the server turns an attachment away.
         */
        alertValidationFailure(error) {
            const message = announcementValidationMessage(error);

            if (!message) return;

            this.$snackbar.open({
                type: 'is-danger',
                position: 'is-bottom-left',
                queue: false,
                message,
                duration: 5000,
            });
        },

        confirmCancel() {
            this.$dialog.confirm({
                title: 'Cancel Announcement',
                message: '<p>By canceling this announcement, changes made within the form will not be saved.</p>',
                confirmText: 'Cancel Announcement',
                cancelText: 'Continue Editing',
                type: 'is-danger',
                onConfirm: () => {
                    this.dirty = false;
                    this.cancel();
                },
                onCancel: () => {
                    this.$emit('close');
                },
            });
        },

        cancel() {
            if (this.dirty) {
                this.confirmCancel();
            } else {
                this.title = null;
                this.message = null;
                this.sendEmailCopy = false;
                this.important = false;
                this.media = [];
                this.editAnnouncementDate = false;
                this.announcementDate = new Date();

                this.toggle();
            }
        },

        clear() {
            this.title = null;
            this.message = null;
            this.sendEmailCopy = false;
            this.media = [];
            this.editAnnouncementDate = false;

            this.toggle();
        },

        post() {
            if (this.sendEmailCopy) {
                const self = this;

                this.$dialog.confirm({
                    title: this.translate('confirm_email'),
                    message: `<p>${this.translate('confirm_email_message')}</p>`,
                    confirmText: this.translate('send_email'),
                    cancelText: this.translate('cancel'),
                    onConfirm: () => {
                        this.createAnnouncement();

                        this.$edverumToast.open(self.translate('emails_sent'));
                    },
                    onCancel: () => {
                        this.sendEmailCopy = false;
                    },
                });
            } else {
                this.createAnnouncement();
            }
        },

        promptDateChange() {
            if (!this.announcementDate) {
                this.announcementDate = new Date();
            }
            this.$dialog.confirm({
                title: 'Confirm Announcement Details',
                message: `
                    Please review the following announcement settings
                    <br><br>
                    <li> Title: ${this.title}</li>
                    <li> Scheduled for: ${
                        this.formatDate(new Date(), 'MM/DD/YYYY hh:mm') ===
                        this.formatDate(this.announcementDate, 'MM/DD/YYYY hh:mm')
                            ? 'Immediately'
                            : this.formatDate(this.announcementDate, 'MM/DD/YYYY hh:mm:ss ')
                    }</li>
                    <li>Notification will be sent: ${this.sendEmailCopy ? 'Yes' : 'No'}</li>
                    <li>Mark as Important: ${this.important ? 'Yes' : 'No'}</li>

                `,
                confirmText: 'Confirm Announcement',
                cancelText: 'Go Back',
                type: 'is-primary',
                hasIcon: false,
                onConfirm: async () => {
                    // Only discard the scheduling and importance choices once the announcement
                    // actually posted. Resetting them unconditionally silently dropped both
                    // whenever publishing failed, while the title, body and attachments stayed.
                    if (!(await this.createAnnouncement())) return;

                    this.editAnnouncementDate = false;
                    this.important = false;

                    this.announcementDate = new Date();
                },
                onCancel: () => {
                    this.editCourseStartDate = false;
                    this.important = false;
                    this.announcementDate = new Date();
                    this.$emit('close');
                },
            });
        },

        toggle() {
            this.displayForm = !this.displayForm;
        },

        async openInstructorChat() {
            if (this.previewMode) return;

            await this.$store.dispatch('Conversation/openNewConversation', this.instructor.id);
        },

        async loadCourseAnnouncements() {
            if (this.previewMode) return;

            this.loading = true;

            try {
                const query = new QueryBuilder(`courses/${this.courseId}/announcements`).include('creator');

                await CourseAnnouncement.api().get(query.toUrl(), { errorHandle: false });
            } catch (e) {
                handleResponseError(e);
            }

            this.loading = false;
        },

        async loadActivityUpdates({ silent = false } = {}) {
            if (this.previewMode || !this.activityId || this._activityUpdatesRequest) {
                return;
            }

            if (silent && this.isBackgroundRefreshPaused) {
                return;
            }

            this._activityUpdatesRequest = true;

            if (!silent && this.isEmpty) {
                this.loading = true;
            }

            try {
                const activityId = Number(this.activityId);

                const query = new QueryBuilder('activity-updates')
                    .filter('activity_id', activityId)
                    .include(['creator', 'media']);

                const result = await ActivityUpdate.api().get(query.toUrl(), {
                    errorHandle: false,
                });

                // ORM api().get() upserts only. Drop this activity's rows that the server no longer returns.
                this.removeStaleActivityUpdates(activityId, result);

                // Read the ORM query directly — `announcements` may still be cached from the loading render.
                const hasUpdates = ActivityUpdate.query().where('activity_id', activityId).get().length > 0;

                this.syncFulfilledVisibility(hasUpdates);
            } catch (e) {
                if (silent) {
                    console.error('[ActivityUpdates] Background refresh failed', e);
                } else {
                    handleResponseError(e);
                }
            } finally {
                if (!silent) {
                    this.loading = false;
                }

                this._activityUpdatesRequest = false;
            }
        },

        formatDate(date, format = 'MM/DD/YYYY hh:mm A') {
            if (!date) return 'Immediately';
            return moment.utc(date).tz(this.$timezone.name).format(format);
        },
    },
};
</script>

<style lang="scss">
.is-important.card,
.card.is-important,
:deep(.is-important.card),
:deep(.card.is-important) {
    border-left: 7px solid #fc6c6c !important;
}

.announcement-drawer {
    :deep(.p-drawer) {
        max-height: 85vh;
        border-radius: 1rem 1rem 0 0;
    }

    :deep(.p-drawer-content) {
        overflow-y: auto;
    }
}

:deep(.announcement-confirm-dialog) {
    width: 100%;
    max-width: 600px;
}

:deep(.announcement-confirm-list) {
    list-style-type: disc;
    padding-left: 2rem;
    margin-top: 1rem;
}
</style>
